import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldOff, ShieldCheck, User, Building2, Truck, Bot, Hand, Save } from "lucide-react";

/**
 * Iter 24 Sub-Fase B — Painel de entidades bloqueadas.
 * Mostra motoristas, parceiros e frotas bloqueados e permite desbloquear.
 * Configura os limiares de bloqueio automatico.
 */
export default function BlockedDashboard() {
  const [data, setData] = useState({ drivers: [], partners: [], fleets: [], count: 0 });
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/blocked"),
    api.get("/admin/blocked/rules"),
  ]).then(([b, r]) => { setData(b.data); setRules(r.data); });

  useEffect(() => { load(); }, []);

  const unblock = async (type, id, name) => {
    if (!window.confirm(`Desbloquear ${name}?`)) return;
    setBusy(id);
    try {
      await api.post(`/admin/${type}s/${id}/unblock`);
      toast.success(`${name} desbloqueado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put("/admin/blocked/rules", rules);
      toast.success("Regras de bloqueio automático guardadas");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!rules) return <div className="p-10 text-slate-500">A carregar…</div>;

  return (
    <div className="p-10 max-w-[1400px]">
      <PageHeader
        eyebrow="Iter 24 · Sub-Fase B"
        title="Entidades Bloqueadas"
        description={`Total actual: ${data.count} · Motoristas ${data.drivers.length} · Parceiros ${data.partners.length} · Frotas ${data.fleets.length}`}
      />

      {/* Auto-block rules */}
      <div className="border border-slate-200 bg-white p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-[#f59e0b]" />
          <div className="label-caps">Regras de bloqueio automático de motoristas</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <RuleField
            label="Rating mínimo"
            hint="Motoristas abaixo deste rating (com 3+ viagens) são bloqueados"
            testId="rule-min-rating"
            value={rules.driver_block_min_rating}
            step="0.1" min="0" max="5"
            onChange={(v) => setRules({ ...rules, driver_block_min_rating: Number(v) })}
          />
          <RuleField
            label="Rejeições consecutivas"
            hint="N rejeições de dispatch em sequência activam bloqueio"
            testId="rule-consecutive-rejects"
            value={rules.driver_block_consecutive_rejects}
            step="1" min="1"
            onChange={(v) => setRules({ ...rules, driver_block_consecutive_rejects: Number(v) })}
          />
          <RuleField
            label="Cancelamentos tardios"
            hint="Nº de cancelamentos com pouca antecedência"
            testId="rule-late-cancels"
            value={rules.driver_block_late_cancels}
            step="1" min="1"
            onChange={(v) => setRules({ ...rules, driver_block_late_cancels: Number(v) })}
          />
          <RuleField
            label="Horas mínimas (cancelamento)"
            hint="Cancelar com menos de X horas conta como tardio"
            testId="rule-cancel-hours"
            value={rules.driver_block_late_cancel_hours}
            step="1" min="1"
            onChange={(v) => setRules({ ...rules, driver_block_late_cancel_hours: Number(v) })}
          />
          <label className="flex items-start gap-3 border border-slate-200 bg-slate-50/40 p-3 cursor-pointer" data-testid="rule-expired-docs">
            <input
              type="checkbox"
              checked={!!rules.driver_block_check_expired_docs}
              onChange={(e) => setRules({ ...rules, driver_block_check_expired_docs: e.target.checked })}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-semibold text-[#0a0f1e]">Bloquear se documentos vencidos</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Sweep diária às 04h00 (Europe/Lisbon)</div>
            </div>
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <GoldButton onClick={saveRules} disabled={saving} data-testid="save-rules">
            <Save className="w-4 h-4" /> {saving ? "…" : "Guardar regras"}
          </GoldButton>
        </div>
      </div>

      {/* Sections */}
      <BlockedSection
        title="Motoristas"
        icon={User}
        items={data.drivers}
        testId="blocked-drivers"
        renderItem={(d) => (
          <>
            <Link to={`/admin/drivers/${d.id}`} className="font-semibold text-[#0a0f1e] hover:text-[#f59e0b]">{d.name}</Link>
            <div className="text-xs text-slate-500">{d.email || d.phone} · Rating {d.rating_avg?.toFixed(1) || "—"}</div>
          </>
        )}
        onUnblock={(id, name) => unblock("driver", id, name)}
        busy={busy}
      />
      <BlockedSection
        title="Parceiros"
        icon={Building2}
        items={data.partners}
        testId="blocked-partners"
        renderItem={(p) => (
          <>
            <Link to={`/admin/partners/${p.id}`} className="font-semibold text-[#0a0f1e] hover:text-[#f59e0b]">{p.company_name}</Link>
            <div className="text-xs text-slate-500">{p.email || p.nif} · Tipo: {p.type}</div>
          </>
        )}
        onUnblock={(id, name) => unblock("partner", id, name)}
        busy={busy}
      />
      <BlockedSection
        title="Frotas"
        icon={Truck}
        items={data.fleets}
        testId="blocked-fleets"
        renderItem={(f) => (
          <>
            <Link to={`/admin/fleets/${f.id}`} className="font-semibold text-[#0a0f1e] hover:text-[#f59e0b]">{f.company_name}</Link>
            <div className="text-xs text-slate-500">{f.email || f.phone} · NIF {f.nif}</div>
          </>
        )}
        onUnblock={(id, name) => unblock("fleet", id, name)}
        busy={busy}
      />
    </div>
  );
}

function RuleField({ label, hint, value, testId, onChange, ...rest }) {
  return (
    <div className="border border-slate-200 bg-slate-50/40 p-3">
      <div className="label-caps mb-1">{label}</div>
      <Input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="input-dt !h-9 font-mono"
        {...rest}
      />
      <div className="text-[10px] text-slate-500 mt-1">{hint}</div>
    </div>
  );
}

function BlockedSection({ title, icon: Icon, items, renderItem, onUnblock, busy, testId }) {
  return (
    <section className="mb-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-slate-700" />
        <div className="label-caps">{title} — {items.length}</div>
      </div>
      {items.length === 0 ? (
        <div className="border border-slate-200 bg-white p-6 text-center text-slate-500 text-sm">Nenhum bloqueado.</div>
      ) : (
        <div className="border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((it) => {
            const auto = it.blocked_auto;
            const name = it.name || it.company_name;
            return (
              <div key={it.id} className="p-4 flex items-start gap-4">
                <div className="flex-1">
                  {renderItem(it)}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] uppercase font-bold ${auto ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                      {auto ? <><Bot className="w-3 h-3 inline mr-1" /> Auto</> : <><Hand className="w-3 h-3 inline mr-1" /> Manual</>}
                    </span>
                    <span className="text-xs text-slate-600">
                      <span className="font-semibold text-red-700">Motivo:</span> {it.block_reason || "—"}
                    </span>
                    {it.blocked_at && (
                      <span className="text-[10px] font-mono text-slate-500">
                        {new Date(it.blocked_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}
                        {it.blocked_by ? ` · por ${it.blocked_by}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <GoldButton
                  onClick={() => onUnblock(it.id, name)}
                  disabled={busy === it.id}
                  data-testid={`unblock-${it.id}`}
                  className="!h-8 !text-[10px]"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> {busy === it.id ? "…" : "Desbloquear"}
                </GoldButton>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Reusable Block modal — used by DriverDetail / PartnerDetail / FleetDetail */
export function BlockModal({ open, onClose, onConfirm, entityLabel }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (!reason.trim()) { toast.error("Indique o motivo do bloqueio"); return; }
    setSaving(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6" data-testid="block-modal">
      <div className="bg-white border border-red-300 w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <ShieldOff className="w-5 h-5 text-red-600" />
          <h3 className="font-display text-lg text-[#0a0f1e]">Bloquear {entityLabel}</h3>
        </div>
        <div className="p-5">
          <label className="label-caps block mb-2">Motivo (obrigatório)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="block-reason"
            className="input-dt w-full"
            placeholder="Ex: falta de pagamento setembro 2026 · comportamento inadequado · …"
          />
          <div className="mt-2 text-[11px] text-slate-500">
            Este utilizador não conseguirá autenticar-se enquanto estiver bloqueado.
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-slate-100">
          <GhostButton onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton onClick={submit} disabled={saving} className="flex-1 justify-center bg-red-600 !text-white hover:!bg-red-700 !border-red-600" data-testid="confirm-block">
            <ShieldOff className="w-4 h-4" /> {saving ? "…" : "Confirmar bloqueio"}
          </GoldButton>
        </div>
      </div>
    </div>
  );
}
